import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type KeyItemPopupTrigger = 'key_item' | 'location_cleared';

export type QueuedOverlay =
  | { type: 'minimumSecured'; itemCount: number }
  | { type: 'roomUnlock'; roomName?: string }
  | { type: 'roomLocked'; roomName?: string }
  | {
      type: 'dinePopup';
      messages: string | string[];
      trigger: KeyItemPopupTrigger;
      itemId?: string;
      autoFadeDelay?: number;
    };

interface RoomOverlayState {
  isVisible: boolean;
  roomName?: string;
}

interface DinePopupOverlayState {
  isVisible: boolean;
  displayId: number;
  messages?: string | string[];
  trigger?: KeyItemPopupTrigger;
  itemId?: string;
  autoFadeDelay?: number;
}

interface OverlayState {
  overlayQueue: QueuedOverlay[];
  roomUnlockOverlay: RoomOverlayState;
  roomLockedOverlay: RoomOverlayState;
  minimumSecuredOverlay: {
    isVisible: boolean;
    itemCount?: number;
  };
  dinePopupOverlay: DinePopupOverlayState;
  locationClearedComplete: boolean;
}

const initialState: OverlayState = {
  overlayQueue: [],
  roomUnlockOverlay: { isVisible: false },
  roomLockedOverlay: { isVisible: false },
  minimumSecuredOverlay: { isVisible: false },
  dinePopupOverlay: { isVisible: false, displayId: 0 },
  locationClearedComplete: false,
};

export const overlaySlice = createSlice({
  name: 'overlay',
  initialState,
  reducers: {
    resetOverlays: () => initialState,

    queueOverlay: (state, action: PayloadAction<QueuedOverlay>) => ({
      ...state,
      overlayQueue: [...state.overlayQueue, action.payload],
    }),

    queueMultipleOverlays: (state, action: PayloadAction<QueuedOverlay[]>) => ({
      ...state,
      overlayQueue: [...state.overlayQueue, ...action.payload],
    }),

    processNextOverlay: (state) => {
      if (state.overlayQueue.length === 0) return state;

      const [nextOverlay, ...remainingQueue] = state.overlayQueue;

      if (nextOverlay.type === 'minimumSecured') {
        return {
          ...state,
          overlayQueue: remainingQueue,
          minimumSecuredOverlay: {
            isVisible: true,
            itemCount: nextOverlay.itemCount,
          },
        };
      } else if (nextOverlay.type === 'roomUnlock') {
        return {
          ...state,
          overlayQueue: remainingQueue,
          roomUnlockOverlay: {
            isVisible: true,
            roomName: nextOverlay.roomName,
          },
        };
      } else if (nextOverlay.type === 'roomLocked') {
        return {
          ...state,
          overlayQueue: remainingQueue,
          roomLockedOverlay: {
            isVisible: true,
            roomName: nextOverlay.roomName,
          },
        };
      } else if (nextOverlay.type === 'dinePopup') {
        return {
          ...state,
          overlayQueue: remainingQueue,
          dinePopupOverlay: {
            isVisible: true,
            displayId: state.dinePopupOverlay.displayId + 1,
            messages: nextOverlay.messages,
            trigger: nextOverlay.trigger,
            itemId: nextOverlay.itemId,
            autoFadeDelay: nextOverlay.autoFadeDelay,
          },
        };
      }

      return {
        ...state,
        overlayQueue: remainingQueue,
      };
    },

    showRoomUnlockOverlay: (
      state,
      action: PayloadAction<{ roomName?: string }>,
    ) => ({
      ...state,
      roomUnlockOverlay: {
        isVisible: true,
        roomName: action.payload.roomName,
      },
    }),

    hideRoomUnlockOverlay: (state) => ({
      ...state,
      roomUnlockOverlay: { isVisible: false },
    }),

    showRoomLockedOverlay: (
      state,
      action: PayloadAction<{ roomName?: string }>,
    ) => ({
      ...state,
      roomLockedOverlay: {
        isVisible: true,
        roomName: action.payload.roomName,
      },
    }),

    hideRoomLockedOverlay: (state) => ({
      ...state,
      roomLockedOverlay: { isVisible: false },
    }),

    showMinimumSecuredOverlay: (
      state,
      action: PayloadAction<{ itemCount: number }>,
    ) => ({
      ...state,
      minimumSecuredOverlay: {
        isVisible: true,
        itemCount: action.payload.itemCount,
      },
    }),

    hideMinimumSecuredOverlay: (state) => ({
      ...state,
      minimumSecuredOverlay: { isVisible: false },
    }),

    showDinePopupOverlay: (
      state,
      action: PayloadAction<{
        messages: string | string[];
        trigger: KeyItemPopupTrigger;
        itemId?: string;
        autoFadeDelay?: number;
      }>,
    ) => ({
      ...state,
      dinePopupOverlay: {
        isVisible: true,
        displayId: state.dinePopupOverlay.displayId + 1,
        messages: action.payload.messages,
        trigger: action.payload.trigger,
        itemId: action.payload.itemId,
        autoFadeDelay: action.payload.autoFadeDelay,
      },
    }),

    hideDinePopupOverlay: (state) => ({
      ...state,
      dinePopupOverlay: {
        isVisible: false,
        displayId: state.dinePopupOverlay.displayId,
      },
      locationClearedComplete:
        state.dinePopupOverlay.trigger === 'location_cleared'
          ? true
          : state.locationClearedComplete,
    }),

    clearLocationClearedComplete: (state) => ({
      ...state,
      locationClearedComplete: false,
    }),

    clearOverlayQueue: (state) => ({
      ...state,
      overlayQueue: [],
    }),
  },
});

export const {
  resetOverlays,
  queueOverlay,
  queueMultipleOverlays,
  processNextOverlay,
  showRoomUnlockOverlay,
  hideRoomUnlockOverlay,
  showRoomLockedOverlay,
  hideRoomLockedOverlay,
  showMinimumSecuredOverlay,
  hideMinimumSecuredOverlay,
  showDinePopupOverlay,
  hideDinePopupOverlay,
  clearLocationClearedComplete,
  clearOverlayQueue,
} = overlaySlice.actions;
export default overlaySlice.reducer;
